import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
}));

const Schedules = () => {
    const classes = useStyles();

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [deletingSchedule, setDeletingSchedule] = useState(null);
    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [schedules, setSchedules] = useState([]);

    useEffect(() => {
        const fetchSchedules = async () => {
            try {
                const { data } = await api.get("/schedules/", {
                    params: { searchParam, pageNumber },
                });
                setSchedules(data.schedules);
                setHasMore(data.hasMore);
                setLoading(false);
            } catch (err) {
                toastError(err);
                setLoading(false);
            }
        };
        fetchSchedules();
    }, [searchParam, pageNumber]);

    useEffect(() => {
        const socket = openSocket();

        socket.on("schedule", (data) => {
            if (data.action === "update" || data.action === "create") {
                setSchedules((prevState) => {
                    const aux = [...prevState];
                    const scheduleIndex = aux.findIndex((s) => s.id === data.schedule.id);
                    if (scheduleIndex !== -1) {
                        aux[scheduleIndex] = data.schedule;
                        return aux;
                    } else {
                        return [data.schedule, ...aux];
                    }
                });
            }

            if (data.action === "delete") {
                setSchedules((prevState) => {
                    return prevState.filter((s) => s.id !== data.scheduleId);
                });
            }
        });

        return () => {
            socket.disconnect();
        };
    }, []);

    const handleOpenScheduleModal = () => {
        setSelectedSchedule(null);
        setScheduleModalOpen(true);
    };

    const handleCloseScheduleModal = () => {
        setSelectedSchedule(null);
        setScheduleModalOpen(false);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleEditSchedule = (schedule) => {
        setSelectedSchedule(schedule);
        setScheduleModalOpen(true);
    };

    const handleDeleteSchedule = async (scheduleId) => {
        try {
            await api.delete(`/schedules/${scheduleId}`);
            toast.success(i18n.t("schedules.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingSchedule(null);
        setSearchParam("");
        setPageNumber(1);
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    return (
        <MainContainer>
            <ConfirmationModal
                title={deletingSchedule && `${i18n.t("schedules.confirmationModal.deleteTitle")} ${deletingSchedule.name}?`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
            >
                {i18n.t("schedules.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <ScheduleModal
                open={scheduleModalOpen}
                onClose={handleCloseScheduleModal}
                scheduleId={selectedSchedule ? selectedSchedule.id : null}
            />
            <MainHeader>
                <Title>{i18n.t("schedules.title")}</Title>
                <MainHeaderButtonsWrapper>
                    <TextField
                        placeholder={i18n.t("schedules.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: "gray" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenScheduleModal}
                    >
                        {i18n.t("schedules.buttons.add")}
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">{i18n.t("schedules.table.name")}</TableCell>
                            <TableCell align="center">{i18n.t("schedules.table.actions")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <>
                            {schedules.map((schedule) => (
                                <TableRow key={schedule.id}>
                                    <TableCell align="center">{schedule.message.substring(0, 50)}...</TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditSchedule(schedule)}
                                        >
                                            <EditIcon />
                                        </IconButton>

                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setDeletingSchedule(schedule);
                                                setConfirmModalOpen(true);
                                            }}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton columns={3} />}
                        </>
                    </TableBody>
                </Table>
            </Paper>
        </MainContainer>
    );
};

export default Schedules;
